/**
 * electricalComplianceCertificatePdfExport.js
 * PDF export for the OC Submission "Electrical Compliance" form
 * (NSW Installation Certificate — Electrical Circuits & Lighting).
 *
 * Follows the app-wide jsPDF + jspdf-autotable + pdfShared.js conventions.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDefaultCompanyInfo, getLOGO_URL, LOGO_FALLBACK_URL } from './brandConfig';
import {
  NAVY, GOLD, WHITE, TEXT_COLOR, MID_GRAY,
  PAGE, CONTENT_WIDTH,
  loadImageAsBase64, ensureSpace, addFooters, savePDF,
  renderNavyHeader, formatDate,
} from './pdfShared';

const ELECTRICAL_INSTALLATION_TEXT = 'The electrical installation has been carried out in accordance with the relevant standards and the approved documentation.';
const ELECTRICAL_INSTALLATION_STANDARDS = 'AS 3000:2018';
const ARTIFICIAL_LIGHTING_TEXT = 'Interior lighting and safe movement lighting installed in accordance with the relevant standards and the approved documentation.';
const ARTIFICIAL_LIGHTING_STANDARDS = 'AS/NZS 1680.0-2009';
const ENERGY_EFFICIENCY_TEXT = 'Artificial lighting and power installed to meet the energy efficiency provisions of the relevant standards and the approved documentation.';
const ENERGY_EFFICIENCY_STANDARDS = 'BCA 2022 Amdt 2 — Clause F6D5, Part J7 & J9';

function sectionBar(doc, y, title) {
  y = ensureSpace(doc, y, 10);
  doc.setFillColor(...GOLD);
  doc.rect(PAGE.margin, y, CONTENT_WIDTH, 7, 'F');
  doc.setTextColor(...NAVY);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text(title.toUpperCase(), PAGE.margin + 2, y + 5);
  return y + 7 + 3;
}

export async function exportElectricalComplianceCertificatePdf(d, project) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const companyInfo = getDefaultCompanyInfo();

  // ── Header ──────────────────────────────────────────────────────────────
  let y = await renderNavyHeader(doc, {
    logoUrl: companyInfo.logo_url || getLOGO_URL(),
    companyName: companyInfo.name,
    address: companyInfo.address,
    phone: companyInfo.phone,
    director: companyInfo.director,
    LOGO_FALLBACK_URL,
  });

  // ── Title block ─────────────────────────────────────────────────────────
  doc.setTextColor(...GOLD);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('INSTALLATION CERTIFICATE', PAGE.margin, y);
  y += 7;

  doc.setTextColor(...NAVY);
  doc.setFontSize(16);
  doc.text('Electrical Circuits & Lighting', PAGE.margin, y);
  y += 3;

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(PAGE.margin, y, PAGE.width - PAGE.margin, y);
  y += 6;

  // ── Project Details ─────────────────────────────────────────────────────
  y = sectionBar(doc, y, 'Project Details');
  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.margin, right: PAGE.margin },
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 2.5, textColor: TEXT_COLOR, lineColor: [229, 228, 223], valign: 'top' },
    columnStyles: { 0: { fontStyle: 'bold', textColor: NAVY, cellWidth: CONTENT_WIDTH / 3 } },
    body: [
      ['Project Name', d.project_name || '—'],
      ['Address', d.address || '—'],
      ['CDC No.', d.cdc_no || '—'],
      ['Lot', d.lot || '—'],
      ['DP', d.dp || '—'],
      ['Building Class', d.building_class || '—'],
      ['Date', d.date ? formatDate(d.date) : '—'],
    ],
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Certification ────────────────────────────────────────────────────────
  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.margin, right: PAGE.margin },
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 2.5, textColor: TEXT_COLOR, lineColor: [229, 228, 223], valign: 'top' },
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 8 },
    columnStyles: {
      0: { fontStyle: 'bold', textColor: NAVY, cellWidth: CONTENT_WIDTH * 0.3 },
      1: { cellWidth: CONTENT_WIDTH * 0.7 },
    },
    head: [['System Installed', 'I certify that the electrical works have been installed to:']],
    body: [
      ['Electrical Installation', `${ELECTRICAL_INSTALLATION_TEXT}\n${ELECTRICAL_INSTALLATION_STANDARDS}`],
      ['Artificial Lighting — Interior Lighting & Safe Movement', `${ARTIFICIAL_LIGHTING_TEXT}\n${ARTIFICIAL_LIGHTING_STANDARDS}`],
      ['Energy Efficiency — Artificial Lighting & Power', `${ENERGY_EFFICIENCY_TEXT}\n${ENERGY_EFFICIENCY_STANDARDS}`],
    ],
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Installer Details ───────────────────────────────────────────────────
  y = sectionBar(doc, y, 'Installer Details');
  autoTable(doc, {
    startY: y,
    margin: { left: PAGE.margin, right: PAGE.margin },
    theme: 'grid',
    styles: { fontSize: 8.5, cellPadding: 2.5, textColor: TEXT_COLOR, lineColor: [229, 228, 223], valign: 'top' },
    columnStyles: { 0: { fontStyle: 'bold', textColor: NAVY, cellWidth: CONTENT_WIDTH / 3 } },
    body: [
      ['Name', d.installer_name || '—'],
      ['Company', d.installer_company || '—'],
      ['Licence', d.installer_licence || '—'],
      ['Phone', d.installer_phone || '—'],
      ['Email', d.installer_email || '—'],
      ['Address', d.installer_address || '—'],
    ],
  });
  y = doc.lastAutoTable.finalY + 6;

  // ── Signature ────────────────────────────────────────────────────────────
  y = ensureSpace(doc, y, 35);
  const sigBoxW = CONTENT_WIDTH * 0.55;
  const sigBoxH = 25;

  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.rect(PAGE.margin, y, sigBoxW, sigBoxH);

  if (d.signature) {
    try {
      const sigImg = await loadImageAsBase64(d.signature);
      if (sigImg) {
        const props = doc.getImageProperties(sigImg);
        const ratio = props.width / props.height;
        let imgH = sigBoxH - 4;
        let imgW = imgH * ratio;
        if (imgW > sigBoxW - 4) {
          imgW = sigBoxW - 4;
          imgH = imgW / ratio;
        }
        doc.addImage(sigImg, 'PNG', PAGE.margin + 2, y + 2, imgW, imgH);
      }
    } catch (e) {
      console.warn('Signature embed failed:', e);
    }
  }

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MID_GRAY);
  doc.text('Signature', PAGE.margin, y + sigBoxH + 4);

  y += sigBoxH + 8;

  // ── Footer ───────────────────────────────────────────────────────────────
  addFooters(doc, companyInfo.name);

  const projectName = project?.name || project?.project_name || 'Project';
  const fileName = `Electrical_Compliance_Certificate_${projectName}.pdf`.replace(/[\\/:*?"<>|]/g, '_');
  await savePDF(doc, fileName);
}
